import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Text, CommandBar, ICommandBarItemProps, Icon, Shimmer, ShimmerElementType } from '@fluentui/react';
import { fetchWorkspaces, getWorkspaceUrls, type Workspace } from '../types/workspace';
import FormField from '../components/FormField';
import styles from './WorkspaceDetail.module.css';

export default function WorkspaceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkspaces()
      .then((all) => {
        const found = all.find((w) => w.code === id);
        setWorkspace(found || null);
      })
      .catch(() => setWorkspace(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className={styles.page}>
        <CommandBar
          items={[{ key: 'back', iconOnly: true, iconProps: { iconName: 'Back' }, onClick: () => { navigate('/workspaces'); }, ariaLabel: 'Back' }]}
          styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }}
        />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Shimmer key={i} shimmerElements={[{ type: ShimmerElementType.line, width: '60%', height: 20 }]} />
          ))}
        </div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className={styles.page}>
        <CommandBar
          items={[{ key: 'back', iconOnly: true, iconProps: { iconName: 'Back' }, onClick: () => { navigate('/workspaces'); }, ariaLabel: 'Back' }]}
          styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }}
        />
        <div className={styles.body}>
          <Text>Workspace not found.</Text>
        </div>
      </div>
    );
  }

  const urls = getWorkspaceUrls(workspace.code);

  const commandBarItems: ICommandBarItemProps[] = [
    { key: 'back', iconOnly: true, iconProps: { iconName: 'Back' }, onClick: () => { navigate('/workspaces'); }, ariaLabel: 'Back' },
    { key: 'newInstance', text: 'New Instance', iconProps: { iconName: 'Add' }, onClick: () => { navigate(`/spawn?workspace=${workspace.code}`); } },
  ];

  const resourceLinks = [
    { key: 'repo', label: 'Repository', url: urls.repoUrl, icon: 'GitGraph' },
    { key: 'pipelines', label: 'Pipelines', url: urls.pipelinesUrl, icon: 'Processing' },
    { key: 'workitems', label: 'Work Items', url: urls.workItemsUrl, icon: 'TaskList' },
    { key: 'wiki', label: 'Wiki', url: urls.wikiUrl, icon: 'ReadingMode' },
    { key: 'sharepoint', label: 'SharePoint', url: urls.sharepointUrl, icon: 'SharepointLogo' },
  ];

  return (
    <div className={styles.page}>
      <CommandBar items={commandBarItems} styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }} />

      <div className={styles.formHeader}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>{workspace.code} — {workspace.name}</Text>
      </div>

      <div className={styles.body}>
        <div className={styles.fieldGrid}>
          <FormField label="Code"><Text>{workspace.code}</Text></FormField>
          <FormField label="Name"><Text>{workspace.name}</Text></FormField>
          <FormField label="Azure DevOps Project">
            <a href={urls.adoProject} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ab-text-link)', textDecoration: 'none' }}>
              {workspace.code}
            </a>
          </FormField>
          <FormField label="SharePoint Team Site">
            <a href={urls.sharepointUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ab-text-link)', textDecoration: 'none' }}>
              {workspace.code}
            </a>
          </FormField>
        </div>

        <div className={styles.section}>
          <Text variant="mediumPlus" className={styles.sectionHeader}>Resources</Text>
          <div className={styles.devOpsLinks}>
            {resourceLinks.map((link) => (
              <a key={link.key} href={link.url} target="_blank" rel="noopener noreferrer" className={styles.devOpsLink}>
                <Icon iconName={link.icon} styles={{ root: { fontSize: 16, color: 'var(--ab-text-link)' } }} />
                <Text>{link.label}</Text>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
